// ============================================================
// RegistroPortal.js — Registro de presença pelo portal (câmera + GPS)
//
// Para quem não usa o app (iOS, ou sem o app instalado). Abre a câmera em
// tempo real (NÃO permite galeria), captura a foto e o GPS, e envia como
// SOLICITAÇÃO — a coordenação aprova, e só então vira presença.
//
// Trata os casos de permissão negada (câmera/localização) com mensagem
// clara. Só registra presença de HOJE, 1 por dia (validado no servidor).
// ============================================================

import { useState, useRef, useEffect, useCallback } from "react";
import { enviarFotoRegistro, registrarPresencaPortal, registrarPresencaEquipe } from "../firebase";
import { BRANCO, CINZA_ESC, VERDE } from "../tema";
import { Card, Btn, StatusBox } from "../componentes";
import { ehAndroid } from "../sistema";

// Mensagem de registro recusado por distância.
//
// O mesmo texto existe no aplicativo, em src/services/localService.js,
// porque portal e aplicativo são projetos separados e não há como
// compartilhar o arquivo. Ao mudar a redação aqui, mudar lá também: o
// atleta que usa iPhone e o que usa Android precisam ler a mesma coisa.
function textoDeBloqueio(r) {
  const m = (n) => (typeof n !== "number" || !isFinite(n) ? "" :
    n < 1000 ? Math.round(n) + " m" : (n / 1000).toFixed(1).replace(".", ",") + " km");
  const onde = r.local ? "de " + r.local : "do local de treino";
  return [
    "Você está a " + m(r.distanciaMetros) + " " + onde +
      ", e o limite para registrar é de " + m(r.raioMetros) + ".",
    "Se você estiver no local e não conseguir registrar, procure o seu técnico ou coordenador para solicitar a regularização da presença.",
    "Aguarde alguns segundos para a localização ser atualizada e tente novamente.",
  ].join("\n\n");
}

// Endereço aproximado a partir das coordenadas, no mesmo serviço que o
// seletor de local já usa. É coluna da planilha: o aplicativo resolve pelo
// sistema do aparelho, e no navegador só dá para perguntar a alguém.
//
// Nunca lança e nunca atrasa o registro: roda em segundo plano depois da
// foto, e se não tiver voltado até o envio o registro vai sem o endereço,
// que é o que já acontecia antes.
async function descobrirEndereco(lat, lng) {
  try {
    const url = "https://nominatim.openstreetmap.org/reverse?format=json&zoom=18&" +
      "lat=" + encodeURIComponent(lat) + "&lon=" + encodeURIComponent(lng);
    // Teto de tempo e identificação do programa. O Nominatim pede quem está
    // consultando e recusa quem não diz; sem teto, a consulta podia ficar
    // pendurada e o envio esperaria por ela.
    const resp = await fetch(url, {
      signal: AbortSignal.timeout ? AbortSignal.timeout(4000) : undefined,
      headers: {
        Accept: "application/json",
        "User-Agent": "RevelarTalentos/1.0 (presenca)",
      },
    });
    if (!resp.ok) return null;
    const dados = await resp.json();
    const a = dados && dados.address;
    if (!a) return dados && dados.display_name ? dados.display_name : null;
    const rua = a.road || a.pedestrian || a.footway || a.suburb || "";
    const numero = a.house_number ? ", " + a.house_number : "";
    const cidade = a.city || a.town || a.village || a.municipality || "";
    const montado = [rua + numero, cidade].filter(Boolean).join(" - ");
    return montado || dados.display_name || null;
  } catch {
    return null;
  }
}

export default function RegistroPortal({ uid, userNucleo, modoEquipe = false }) {
  // O portal existe para quem não consegue instalar o aplicativo. Quem está
  // no Android consegue, então é orientado a usá-lo.
  //
  // A checagem é "é Android?", nunca "é iOS?". O iPad se identifica como
  // "Macintosh" desde o iPadOS 13, então uma regra que só liberasse quem
  // parecesse iOS barraria iPads legítimos. Na dúvida, libera.
  //
  // Isto não é segurança: o navegador informa o sistema num cabeçalho que
  // se troca em dois cliques. Resolve o uso por comodidade, não a má-fé.
  // A equipe técnica não entra nessa regra, porque para ela o portal é o
  // caminho normal de registro.
  const bloqueadoPorAndroid = !modoEquipe && ehAndroid();

  const [aberto, setAberto] = useState(false);
  const [cameraAtiva, setCameraAtiva] = useState(false);
  const [fotoBlob, setFotoBlob] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [localizacao, setLocalizacao] = useState(null);
  const [status, setStatus] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  // A busca do endereço, ainda em curso. Antes era disparada e esquecida: se
  // a pessoa tocasse em Enviar antes da resposta chegar — o normal, porque a
  // foto acabou de ser tirada —, o registro ia sem endereço, e a planilha
  // saía com a coluna em branco em 1 de cada 3 registros do portal.
  const enderecoEmCurso = useRef(null);

  // Encerra a câmera (libera o hardware).
  const pararCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraAtiva(false);
  }, []);

  useEffect(() => () => pararCamera(), [pararCamera]); // limpa ao desmontar

  // Conecta o stream ao <video> SÓ depois que o elemento existe no DOM.
  // No Safari iOS, atribuir srcObject antes do <video> estar renderizado
  // resulta em câmera ligada (luz acesa) mas tela preta. Este efeito roda
  // após a renderização, quando videoRef.current já aponta para o elemento.
  useEffect(() => {
    const video = videoRef.current;
    if (cameraAtiva && video && streamRef.current) {
      video.srcObject = streamRef.current;
      // Safari exige play() após um gesto do usuário e com o elemento pronto.
      const tentarPlay = () => {
        video.play().catch(() => {
          // Alguns Safari só liberam o play no loadedmetadata.
          video.onloadedmetadata = () => video.play().catch(() => {});
        });
      };
      if (video.readyState >= 1) tentarPlay();
      else video.onloadedmetadata = tentarPlay;
    }
  }, [cameraAtiva]);

  async function abrirCamera() {
    setStatus(null);
    setFotoBlob(null);
    setFotoPreview(null);
    try {
      // facingMode "user" = câmera frontal (selfie). getUserMedia só acessa
      // a câmera ao vivo — não há como escolher da galeria por aqui.
      // A conexão ao elemento <video> é feita no useEffect (após render),
      // o que é essencial para o Safari iOS não mostrar tela preta.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" }, audio: false,
      });
      streamRef.current = stream;
      setCameraAtiva(true); // dispara o useEffect que conecta o stream ao vídeo
    } catch (e) {
      const nome = e?.name || "";
      let msg = "Não foi possível acessar a câmera. Verifique a permissão no navegador e tente de novo.";
      if (nome === "NotAllowedError" || nome === "SecurityError") {
        msg = "Permissão da câmera negada. Vá em Ajustes do navegador e permita o acesso à câmera para este site.";
      } else if (nome === "NotFoundError" || nome === "OverconstrainedError") {
        msg = "Câmera não encontrada neste aparelho.";
      } else if (nome === "NotReadableError") {
        msg = "A câmera está em uso por outro aplicativo. Feche os outros apps que usam a câmera e tente de novo.";
      }
      setStatus({ tipo: "erro", msg });
    }
  }

  function capturarFoto() {
    const video = videoRef.current;
    if (!video) return;
    // Se o vídeo ainda não tem dimensões (não carregou), avisa em vez de
    // gerar uma foto preta/vazia.
    if (!video.videoWidth || !video.videoHeight) {
      setStatus({ tipo: "erro", msg: "A câmera ainda está carregando. Aguarde um instante e tente novamente." });
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob(blob => {
      setFotoBlob(blob);
      setFotoPreview(URL.createObjectURL(blob));
      pararCamera();
      // Após a foto, tenta capturar a localização.
      pegarLocalizacao();
    }, "image/jpeg", 0.85);
  }

  // silencioso: não mexe no status. Usado na retentativa depois de um
  // bloqueio, onde a mensagem que explica o bloqueio precisa continuar na
  // tela enquanto a posição é buscada de novo por baixo.
  function pegarLocalizacao(silencioso = false) {
    if (!navigator.geolocation) {
      setStatus({ tipo: "erro", msg: "Seu aparelho não fornece localização." });
      return;
    }
    if (!silencioso) setStatus({ tipo: "ok", msg: "Obtendo localização..." });
    navigator.geolocation.getCurrentPosition(
      pos => {
        const { latitude, longitude } = pos.coords;
        setLocalizacao({
          lat: latitude,
          lng: longitude,
          // O navegador informa a margem de erro da própria medição. Vai
          // junto porque o servidor libera o registro quando o aparelho
          // declara que a medição é ruim, e sem este número o portal seria
          // bloqueado em situações que o aplicativo deixa passar.
          precisaoMetros: typeof pos.coords.accuracy === "number" ? pos.coords.accuracy : null,
        });
        enderecoEmCurso.current = descobrirEndereco(latitude, longitude).then(nome => {
          if (nome) setLocalizacao(atual => (atual ? { ...atual, nomeDoLugar: nome } : atual));
          return nome || null;
        });
        if (!silencioso) setStatus(null);
      },
      () => { if (!silencioso) setStatus({ tipo: "erro", msg: "Não foi possível obter a localização. Permita o acesso e tente de novo." }); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  function refazer() {
    setFotoBlob(null);
    setFotoPreview(null);
    setLocalizacao(null);
    setStatus(null);
    abrirCamera();
  }

  async function enviar() {
    if (!fotoBlob) { setStatus({ tipo: "erro", msg: "Tire a foto primeiro." }); return; }
    if (!localizacao) { setStatus({ tipo: "erro", msg: "Localização obrigatória. Permita o acesso e tente de novo." }); return; }
    setEnviando(true);
    setStatus({ tipo: "ok", msg: "Enviando registro..." });
    try {
      const up = await enviarFotoRegistro({ uid, nucleo: userNucleo, blob: fotoBlob });

      // Espera o endereço que já foi pedido lá atrás, logo depois da foto.
      // Quase sempre já chegou e isto não custa nada; quando não chegou, o
      // envio da foto deu tempo. Se falhar ou passar do teto, segue sem:
      // o servidor tenta de novo, e no fim latitude, longitude e link do
      // mapa cobrem a lacuna.
      const nomeDoLugar = localizacao.nomeDoLugar
        || (enderecoEmCurso.current ? await enderecoEmCurso.current.catch(() => null) : null);

      const envio = {
        fotoUrl: up.url, fotoCaminho: up.caminho,
        lat: localizacao.lat, lng: localizacao.lng,
        precisaoMetros: localizacao.precisaoMetros,
        nomeDoLugar: nomeDoLugar || null,
      };
      const resultado = modoEquipe
        ? await registrarPresencaEquipe(envio)
        : await registrarPresencaPortal(envio);

      // Bloqueio não é erro, é resposta do servidor: a presença não foi
      // gravada porque o registro caiu fora do raio. Antes esta resposta
      // era ignorada e a tela dizia "registrada com sucesso" do mesmo
      // jeito, o que seria a pior mentira possível aqui.
      if (resultado && resultado.bloqueado) {
        setStatus({ tipo: "erro", msg: textoDeBloqueio(resultado) });
        // Busca a posição de novo, por baixo. Sem isto, "tente novamente"
        // reenviaria exatamente as mesmas coordenadas e seria bloqueado
        // igual, para sempre.
        pegarLocalizacao(true);
        return;
      }

      setStatus({ tipo: "ok", msg: "Presença registrada com sucesso!" });
      setFotoBlob(null);
      setFotoPreview(null);
      setLocalizacao(null);
      setAberto(false);
    } catch (e) {
      const msg = String(e?.message || "");
      // A recusa por rosto não é erro de sistema: é pedido de outra foto, e
      // o texto é o mesmo que o atleta vê no aplicativo e no cartão de
      // reconferência. A foto e a localização ficam, para ele só tirar
      // outra sem refazer o caminho todo.
      if (msg.includes("ROSTO_NAO_CONFERE")) {
        setStatus({
          tipo: "erro",
          msg: "Não conseguimos confirmar sua identidade pela foto enviada. "
            + "Tire uma nova foto de frente, com o rosto bem iluminado, sem boné. "
            + "Evite luz forte atrás de você.",
        });
        setFotoBlob(null);
        setFotoPreview(null);
      } else {
        setStatus({ tipo: "erro", msg: msg || "Não foi possível enviar." });
      }
    } finally {
      setEnviando(false);
    }
  }

  // No Android o cartão vira orientação: o registro acontece no aplicativo,
  // que tem a câmera e o GPS do aparelho e não depende de aprovação depois.
  if (bloqueadoPorAndroid) {
    return (
      <Card>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Registre pelo aplicativo</div>
        <div style={{ fontSize: 13, color: CINZA_ESC, lineHeight: 1.6 }}>
          Este registro pelo portal existe para quem está no iPhone ou iPad, onde o
          aplicativo não está disponível. No Android, use o <b>aplicativo Revelar
          Talentos</b>, que tem acesso melhor à câmera e ao GPS do aparelho.
        </div>
        <div style={{ fontSize: 12, color: CINZA_ESC, marginTop: 10, lineHeight: 1.6 }}>
          Ainda não instalou? O link de download está na tela inicial do portal.
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Registro para iOS</div>
      <div style={{ fontSize: 12, color: CINZA_ESC, marginBottom: 12, lineHeight: 1.6 }}>
        Está sem o aplicativo (iPhone/iOS)? Registre sua presença de hoje aqui, com foto e localização.
        {" Só é possível registrar o dia de hoje."}
      </div>

      {status && <div style={{ marginBottom: 12 }}><StatusBox tipo={status.tipo} msg={status.msg} /></div>}

      {!aberto ? (
        <Btn onClick={() => setAberto(true)}>📸 Registrar presença de hoje</Btn>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Câmera ao vivo — o <video> fica SEMPRE no DOM (apenas oculto
              quando inativo) para que videoRef exista quando o stream é
              conectado. Ocultar via display evita a tela preta do Safari. */}
          <div style={{ display: cameraAtiva ? "block" : "none" }}>
            <video
              ref={videoRef}
              playsInline
              autoPlay
              muted
              webkit-playsinline="true"
              style={{ width: "100%", maxWidth: 360, borderRadius: 12, background: "#000" }}
            />
            <div style={{ marginTop: 10 }}>
              <Btn onClick={capturarFoto}>📷 Tirar foto</Btn>
            </div>
          </div>

          {/* Prévia da foto tirada */}
          {fotoPreview && (
            <div>
              <img src={fotoPreview} alt="Prévia" style={{ width: "100%", maxWidth: 360, borderRadius: 12 }} />
              <div style={{ fontSize: 12, color: localizacao ? VERDE : CINZA_ESC, marginTop: 6 }}>
                {localizacao ? "✓ Localização capturada" : "Aguardando localização..."}
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                <Btn onClick={enviar} disabled={enviando || !localizacao}>
                  {enviando ? "Enviando…" : "Enviar registro"}
                </Btn>
                <button onClick={refazer} disabled={enviando}
                  style={{ background: BRANCO, border: `1.5px solid ${CINZA_ESC}`, color: CINZA_ESC, borderRadius: 9, padding: "9px 16px", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>
                  Refazer
                </button>
              </div>
            </div>
          )}

          {/* Estado inicial: abrir a câmera */}
          {!cameraAtiva && !fotoPreview && (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Btn onClick={abrirCamera}>Abrir câmera</Btn>
              <button onClick={() => { setAberto(false); setStatus(null); }}
                style={{ background: "none", border: "none", color: CINZA_ESC, fontSize: 13, cursor: "pointer" }}>
                Cancelar
              </button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}